import {
  type BlockInteractionsBlockBy,
  type BlockInteractionsBlockingTypes,
  IFindAndReplaceRuleType,
  type ILinkReplaceRule,
  type LinkEditBy,
  type LinkEditTypes,
  type Modification,
  NavigationEditSchemaType,
  RuleApplyOnType,
  type SelectorType,
  type SelectorTypeToSelector,
} from '@carboncopyco/types';
import { v4 as uuid } from 'uuid';
import {
  ATTRIBUTES,
  DS_BLOCK_INTERACTIONS_HREF,
  DS_JAVASCRIPT_VOID,
  DS_REPLACE_LINK_ORIGINAL_HREF,
  STORED_HREF_KEY,
} from './constants';
import { isAnchorElement, isButtonElement } from './dom';
import { getDecodedUrlFromHref } from './domains';
import { createSelector } from './selectors';

export const getTargetElementType = (
  target: HTMLElement
): BlockInteractionsBlockingTypes | undefined => {
  if (isAnchorElement(target)) {
    return 'link';
  }
  if (isButtonElement(target)) {
    return 'button';
  }

  return undefined;
};

export const getSelectorTagName = (
  elementType: LinkEditTypes
): keyof HTMLElementTagNameMap | undefined => {
  if (elementType === 'button') {
    return 'button';
  }
  if (elementType === 'link') {
    return 'a';
  }
  return undefined;
};

export type SelectorsToArguments = {
  [key in keyof SelectorTypeToSelector]: Partial<
    SelectorTypeToSelector[key]['arguments']
  >;
};

type CreateBlockInteractionsModification = {
  blockAcross: RuleApplyOnType;
  blockingType: BlockInteractionsBlockingTypes;
  blockBy: BlockInteractionsBlockBy;
  pageUrl: string;
  elementData: Record<string, unknown>;
};
type CreateReplaceLinkModification = {
  applyOn: RuleApplyOnType;
  elementType: LinkEditTypes;
  findBy: BlockInteractionsBlockBy;
  pageUrl: string;
  newValue: string;
  elementData: Record<string, unknown>;
  isExternal: boolean;
};

export const getSelectorArguments = ({
  pageUrl,
  textContent,
  href,
  applyOn,
  findBy,
  elementType,
}: {
  elementType: LinkEditTypes;
  textContent?: string;
  href?: string;
  pageUrl: string;
  applyOn: RuleApplyOnType;
  findBy: LinkEditBy;
}) => {
  const isApplyOnCurrentPage = applyOn === RuleApplyOnType.currentPage;
  const findBySelector: SelectorType =
    findBy === 'title' ? 'text_content' : 'anchor_href';

  const config = {
    tag: {
      tagName: getSelectorTagName(elementType),
    },
    page_url: {
      url: pageUrl,
    },
    anchor_href: {
      url: href,
      mode: 'exact',
    },
    text_content: {
      text: textContent,
    },
  } as SelectorsToArguments;
  const selectedSelectors = [
    'tag',
    findBySelector,
    ...(isApplyOnCurrentPage ? (['page_url'] satisfies SelectorType[]) : []),
  ] satisfies SelectorType[];
  return selectedSelectors.map((selector) =>
    createSelector(selector, config[selector])
  );
};

export function createBlockInteractionsModification({
  blockAcross,
  blockingType,
  blockBy,
  elementData,
  pageUrl,
}: CreateBlockInteractionsModification): Partial<
  Modification<NavigationEditSchemaType.blockInteractions>
> {
  const href = String(elementData.href);
  const textContent = String(elementData.textContent);

  return {
    selectors: getSelectorArguments({
      elementType: blockingType,
      pageUrl,
      textContent,
      href,
      applyOn: blockAcross,
      findBy: blockBy,
    }),
    modificationType: 'navigation_rule',
    content: {
      type: NavigationEditSchemaType.blockInteractions,
      applyOn: blockAcross,
      ruleId: uuid(),
      blockingType,
      blockBy,
      blockingValue: blockBy === 'title' ? textContent : href,
      elementData: {
        ...elementData,
        pageUrl,
      },
    },
  };
}

export function createReplaceLinkModification({
  applyOn,
  elementType,
  findBy,
  elementData,
  pageUrl,
  isExternal,
  newValue,
}: CreateReplaceLinkModification) {
  const href = String(elementData.href);
  const textContent = String(elementData.textContent);
  const originalHref = elementData.originalHref as string | undefined;

  return {
    selectors: getSelectorArguments({
      elementType,
      textContent,
      href: originalHref ?? href,
      pageUrl,
      applyOn,
      findBy,
    }),
    modificationType: 'navigation_rule',
    content: {
      type: IFindAndReplaceRuleType.replaceLink,
      applyOn: applyOn,
      ruleId: uuid(),
      replaceType: elementType,
      findBy,
      oldValue: findBy === 'title' ? textContent : originalHref ?? href,
      newValue,
      elementData: {
        ...elementData,
        pageUrl,
      },
      isExternal,
    },
  };
}

export const getTitleLabel = (type: LinkEditTypes) => {
  return `Find all ${type}s titled with`;
};
export const getLinkLabel = () => {
  return 'Find all links to';
};

export const findByDropdownItems = [
  {
    key: 'url',
    value: getLinkLabel(),
  },
  {
    key: 'title',
    value: getTitleLabel('link'),
  },
];

export const getHrefFromTarget = (target: HTMLAnchorElement) => {
  const href = target.getAttribute('href');
  if (href === DS_JAVASCRIPT_VOID) {
    const storedHref = target.getAttribute(STORED_HREF_KEY);
    if (storedHref && storedHref !== DS_JAVASCRIPT_VOID) {
      return storedHref;
    }
    return target.getAttribute(DS_BLOCK_INTERACTIONS_HREF);
  }
  return href;
};

export const getElementMetadata = (target: HTMLElement) => {
  const href =
    (isAnchorElement(target) &&
      getDecodedUrlFromHref(
        getHrefFromTarget(target as HTMLAnchorElement) ?? ''
      )) ||
    null;

  return {
    textContent: target.textContent?.trim(),
    href: href?.trim(),
    originalHref: target.getAttribute(DS_REPLACE_LINK_ORIGINAL_HREF),
    findBy: target.getAttribute(
      ATTRIBUTES.DS_FIND_BY
    ) as ILinkReplaceRule['findBy'],
  };
};

export const isLinkValid = (href: string) => {
  const isJSVoid = new RegExp(/javascript\s*:\s*void/gm).test(href); // need to test with Regex due to diffrent implementations.
  return href && !!href.length && !isJSVoid && href !== 'null';
};
